import { ParameterError } from '../../types/errors';
import { isAndFilterGroup, isOrFilterGroup, } from '../../types/filter';
import { getSourceQueryResultReferences, QuerySourceType, } from '../../types/querySources';
import assertUnreachable from '../../utils/assertUnreachable';
import { stableJson } from '../../utils/stableJson';
export const toComposerArtifactVersion = ({ title, description, queries, terminalNodeId, vizConfig, }) => ({
    artifactType: 'chart',
    title,
    ...(description ? { description } : {}),
    vizConfig: {
        source: 'composer',
        schemaVersion: 1,
        queries,
        terminalNodeId,
        vizConfig,
    },
});
export const toComposerChartArtifactVersion = ({ title, description, queries, terminalNodeId, vizConfig, }) => ({
    artifactType: 'chart',
    title,
    ...(description ? { description } : {}),
    vizConfig: {
        source: 'composer',
        schemaVersion: 2,
        queries,
        terminalNodeId,
        vizConfig,
    },
});
export const isAiComposerChartArtifactConfig = (config) => typeof config === 'object' &&
    config !== null &&
    'source' in config &&
    config.source === 'composer' &&
    'schemaVersion' in config &&
    (config.schemaVersion === 1 || config.schemaVersion === 2) &&
    'queries' in config &&
    Array.isArray(config.queries) &&
    'terminalNodeId' in config &&
    typeof config.terminalNodeId === 'string';
/** Semantic explores a pipeline reads, the access a user needs to see its results. */
export const getComposerSemanticExploreNames = (queries) => [
    ...new Set(queries.flatMap((node) => node.sourceType === QuerySourceType.SEMANTIC_LAYER
        ? [node.exploreName]
        : [])),
];
// The full definition, with array references written in map form.
// Filter group and rule ids are generated per submission, so two identical
// filters differ only by id; compare them without.
const filterItemDefinition = (item) => {
    if (isAndFilterGroup(item)) {
        return { and: item.and.map(filterItemDefinition) };
    }
    if (isOrFilterGroup(item)) {
        return { or: item.or.map(filterItemDefinition) };
    }
    const { id, ...rule } = item;
    return rule;
};
const filtersDefinition = (filters) => filters === undefined
    ? undefined
    : {
        dimensions: filters.dimensions &&
            filterItemDefinition(filters.dimensions),
        metrics: filters.metrics && filterItemDefinition(filters.metrics),
        tableCalculations: filters.tableCalculations &&
            filterItemDefinition(filters.tableCalculations),
    };
const nodeDefinition = (node) => {
    switch (node.sourceType) {
        case QuerySourceType.DUCKDB:
        case QuerySourceType.PYTHON:
            return stableJson({
                ...node,
                references: getSourceQueryResultReferences(node),
            });
        case QuerySourceType.SEMANTIC_LAYER:
            return stableJson({
                ...node,
                filters: filtersDefinition(node.filters),
            });
        case QuerySourceType.SQL:
        case QuerySourceType.EXTERNAL:
        case QuerySourceType.API:
            return stableJson(node);
        default:
            return assertUnreachable(node, 'Unknown composer node');
    }
};
export const isSameComposerNodeDefinition = (a, b) => nodeDefinition(a) === nodeDefinition(b);
export const getComposerNodeConflictMessage = (existing) => `Node id "${existing.nodeId}" already names a different node in this thread ("${existing.title ?? existing.nodeId}"). To reuse it, read "${existing.nodeId}" without resubmitting it; to define a new node, give it a different node id.`;
const describeNodes = (nodes) => nodes.length === 0
    ? 'No nodes have been defined earlier in this thread.'
    : `Nodes defined earlier in this thread: ${nodes
        .map((node) => `${node.nodeId} ("${node.title ?? node.nodeId}")`)
        .join(', ')}.`;
export const toComposerNodeColumns = (columns) => Object.values(columns).map(({ reference, type }) => ({ reference, type }));
const hasNodeId = (node) => !!node.nodeId;
/**
 * Builds a submission's pipeline: submitted nodes plus every reused thread
 * node and everything it reads, in dependency order. A node id names one
 * definition for the whole thread.
 */
export const buildComposerArtifactPipeline = ({ queries, threadNodes: threadNodeList, }) => {
    const threadNodes = new Map(threadNodeList.map((node) => [node.nodeId, node]));
    const submittedNodes = new Map(queries.flatMap((node) => (node.nodeId ? [[node.nodeId, node]] : [])));
    submittedNodes.forEach((node, nodeId) => {
        const existing = threadNodes.get(nodeId);
        if (existing && !isSameComposerNodeDefinition(existing, node)) {
            throw new ParameterError(getComposerNodeConflictMessage(existing));
        }
    });
    queries.forEach((node) => Object.values(getSourceQueryResultReferences(node)).forEach((reference) => {
        if (!submittedNodes.has(reference) &&
            !threadNodes.has(reference)) {
            throw new ParameterError(`Node "${node.nodeId}" reads "${reference}", which is not a node of this submission or of an earlier call in this thread. ${describeNodes(threadNodeList)}`);
        }
    }));
    const emitted = new Set();
    const visiting = new Set();
    const pipeline = [];
    const visit = (node) => {
        if (!hasNodeId(node))
            return;
        const { nodeId } = node;
        if (emitted.has(nodeId) || visiting.has(nodeId))
            return;
        visiting.add(nodeId);
        Object.values(getSourceQueryResultReferences(node)).forEach((reference) => {
            const read = submittedNodes.get(reference) ?? threadNodes.get(reference);
            if (read)
                visit(read);
        });
        visiting.delete(nodeId);
        emitted.add(nodeId);
        pipeline.push(node);
    };
    queries.forEach(visit);
    return pipeline;
};
/** Identity of a pinned composer pipeline node as prompt context. */
export const composerNodeContextKey = ({ artifactUuid, versionUuid, nodeId, }) => `composer_node:${artifactUuid}:${versionUuid}:${nodeId}`;
//# sourceMappingURL=composerArtifact.js.map